import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit, ExternalLink, Search, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '~/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { apiClient, type Category } from '~/lib/api';

export default function CategoriesPage() {
  const queryClient = useQueryClient();
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({ slug: '', name: '', jpName: '', zhName: '' });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-categories', page, search],
    queryFn: () => apiClient.getCategories({ page, limit, search }),
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; slug: string; name: string; jpName?: string; zhName?: string }) =>
      apiClient.updateCategory(data.id, { slug: data.slug, name: data.name, jpName: data.jpName, zhName: data.zhName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      setEditingCategory(null);
      setFormData({ slug: '', name: '', jpName: '', zhName: '' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      setDeletingCategory(null);
    },
  });

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      slug: category.slug,
      name: category.name,
      jpName: category.jpName || '',
      zhName: category.zhName || '',
    });
  };

  const handleSave = () => {
    if (editingCategory && formData.slug && formData.name) {
      updateMutation.mutate({
        id: editingCategory.id,
        slug: formData.slug,
        name: formData.name,
        jpName: formData.jpName || undefined,
        zhName: formData.zhName || undefined,
      });
    }
  };

  const handleDelete = () => {
    if (deletingCategory) {
      deleteMutation.mutate(deletingCategory.id);
    }
  };

  const handleSearchSubmit = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const getCategoryUrl = (slug: string) => {
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://javfacts.com';
    return `${baseUrl}/en/categories/${slug}`;
  };

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold tracking-tight'>Categories Management</h1>
        <p className='text-muted-foreground'>Browse and manage all categories/tags in the database</p>
      </div>

      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle>All Categories</CardTitle>
              <CardDescription>{data?.data ? `${data.data.total} categories total` : 'Loading...'}</CardDescription>
            </div>
            <div className='flex items-center gap-2'>
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  placeholder='Search by slug or name...'
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchSubmit();
                    }
                  }}
                  onBlur={handleSearchSubmit}
                  className='pl-9 w-[300px]'
                />
                {searchInput && (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0'
                    onClick={handleSearchClear}
                  >
                    <X className='h-4 w-4' />
                  </Button>
                )}
              </div>
              {search && (
                <Button variant='secondary' size='sm' onClick={handleSearchClear}>
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='text-center py-8'>
              <p className='text-muted-foreground'>Loading categories...</p>
            </div>
          ) : error ? (
            <div className='text-center py-8'>
              <p className='text-destructive'>Error loading categories</p>
            </div>
          ) : data?.data?.items.length === 0 ? (
            <div className='text-center py-8'>
              <p className='text-muted-foreground'>
                {search ? 'No categories match your search' : 'No categories found'}
              </p>
              {search && (
                <Button
                  variant='link'
                  onClick={() => {
                    setSearch('');
                    setPage(1);
                  }}
                  className='mt-2'
                >
                  Clear search
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='w-[150px]'>Slug</TableHead>
                      <TableHead>English Name</TableHead>
                      <TableHead>Japanese Name</TableHead>
                      <TableHead>Chinese Name</TableHead>
                      <TableHead>Videos</TableHead>
                      <TableHead className='w-[200px]'>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.data?.items.map((category: any) => (
                      <TableRow key={category.id}>
                        <TableCell className='font-mono text-xs'>{category.slug}</TableCell>
                        <TableCell>
                          <span className='font-medium'>{category.name}</span>
                        </TableCell>
                        <TableCell>
                          {category.jpName ? (
                            <span className='text-sm'>{category.jpName}</span>
                          ) : (
                            <span className='text-muted-foreground text-sm'>N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {category.zhName ? (
                            <span className='text-sm'>{category.zhName}</span>
                          ) : (
                            <span className='text-muted-foreground text-sm'>N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant='secondary'>{category._count.videos} videos</Badge>
                        </TableCell>
                        <TableCell>
                          <div className='flex items-center gap-2'>
                            <Button variant='ghost' size='sm' onClick={() => handleEdit(category)}>
                              <Edit className='h-4 w-4' />
                            </Button>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => setDeletingCategory(category)}
                              className='text-destructive hover:text-destructive'
                            >
                              <Trash2 className='h-4 w-4' />
                            </Button>
                            <a
                              href={getCategoryUrl(category.slug)}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
                            >
                              <ExternalLink className='h-4 w-4' />
                            </a>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {data?.data && data.data.totalPages > 1 && (
                <div className='flex items-center justify-between mt-4'>
                  <p className='text-sm text-muted-foreground'>
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, data.data.total)} of {data.data.total}{' '}
                    categories
                  </p>
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPage((p) => Math.min(data.data.totalPages, p + 1))}
                      disabled={page === data.data.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Sheet */}
      <Sheet open={!!editingCategory} onOpenChange={(open) => !open && setEditingCategory(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit Category</SheetTitle>
            <SheetDescription>Update category information</SheetDescription>
          </SheetHeader>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='slug'>
                Slug <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='slug'
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder='category-slug'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='name'>
                English Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='name'
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder='Category Name'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='jpName'>Japanese Name</Label>
              <Input
                id='jpName'
                value={formData.jpName}
                onChange={(e) => setFormData({ ...formData, jpName: e.target.value })}
                placeholder='カテゴリー名'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='zhName'>Chinese Name</Label>
              <Input
                id='zhName'
                value={formData.zhName}
                onChange={(e) => setFormData({ ...formData, zhName: e.target.value })}
                placeholder='分类名称'
              />
            </div>
          </div>
          <SheetFooter>
            <Button variant='outline' onClick={() => setEditingCategory(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!formData.slug || !formData.name || updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingCategory} onOpenChange={(open) => !open && setDeletingCategory(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "<strong>{deletingCategory?.name}</strong>"?
              {deletingCategory && deletingCategory._count.videos > 0 && (
                <span className='block mt-2 text-destructive font-semibold'>
                  This category has {deletingCategory._count.videos} associated videos and cannot be deleted.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deletingCategory?._count.videos! > 0 || deleteMutation.isPending}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
